import Point from "@arcgis/core/geometry/Point";
import { geographicToWebMercator } from "@arcgis/core/geometry/support/webMercatorUtils";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MapView from "@arcgis/core/views/MapView";
import {
  WORLD_MAP_DEFAULT_MAP_STYLE,
  WORLD_MAP_MARKER_HEIGHT_PX,
  WORLD_MAP_MARKER_WIDTH_PX,
  getWorldMapGeoJSONLayerConfig,
} from "@components/world-map/world-map-constants";
import {
  GetLayerProps,
  MapStyle,
  MarkerPopupHorizontalPlacement,
  MarkerPopupPlacement,
  WorldMapEntity,
  WorldMapFeature,
  WorldMapFeatureCollection,
  WorldMapGeoJSONLayerParams,
} from "@components/world-map/world-map-types";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { isMapStyle } from "@custom-types/world-map-type-guards";

/** Local storage key under which the selected world map style is persisted. */
const MAP_STYLE_STORAGE_KEY = "map-style";

/** Number of horizontal sections used to decide popup side placement. */
const MARKER_POPUP_HORIZONTAL_SECTIONS = 3;

/** Minimum vertical space required above a marker so the popup can render on top. */
const MARKER_POPUP_MIN_TOP_SPACE_PX = 275;

/** Extra safe margin from the map top edge when placing marker popups. */
const MARKER_POPUP_TOP_SAFE_MARGIN_PX = 8;

/** Vertical gap between marker and popup card. */
const WORLD_MAP_MARKER_POPUP_GAP_PX = 2;

/**
 * Converts a world map entity to a GeoJSON Feature object representing the entity on the world map.
 * https://datatracker.ietf.org/doc/html/rfc7946#section-3.2
 * @param entity - World map entity
 * @returns GeoJSON feature object representing the entity
 */
function getWorldMapFeature<T extends WorldMapEntity>(
  entity: T
): WorldMapFeature<T> {
  return {
    id: entity.id,
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [entity.position.long, entity.position.lat],
    },
    properties: entity,
  };
}

/**
 * Converts world map entities to a GeoJSON FeatureCollection object representing multiple entities on the world map.
 * https://datatracker.ietf.org/doc/html/rfc7946#section-3.3
 * @param entities - Array of entities
 * @returns GeoJSON FeatureCollection object representing the entities
 */
function getWorldMapFeatureCollection<T extends WorldMapEntity>(
  entities: T[]
): WorldMapFeatureCollection<T> {
  return {
    type: "FeatureCollection",
    features: entities.map(getWorldMapFeature),
  };
}

/**
 * Creates a URL for the world map GeoJSON layer from the world map feature collection.
 * ArcGIS GeoJSONLayer requires a URL, not an object.
 * @param featureCollection - World map feature collection
 * @returns URL of the Blob containing the GeoJSON layer data
 */
function getWorldMapGeoJSONLayerUrl<T extends WorldMapEntity>(
  featureCollection: WorldMapFeatureCollection<T>
): string {
  const blob = new Blob([JSON.stringify(featureCollection)], {
    type: "application/json",
  });
  return URL.createObjectURL(blob);
}

/**
 * Creates a instance of the GeoJSONLayer class.
 * @returns GeoJSONLayer instance
 */
function getWorldMapGeoJSONLayer<T extends WorldMapEntity>(
  params: WorldMapGeoJSONLayerParams<T>,
  mapStyle: MapStyle
): GeoJSONLayer {
  return new GeoJSONLayer({
    ...params,
    ...getWorldMapGeoJSONLayerConfig(mapStyle),
  });
}

/** Return type for the getLayer function */
export interface GetLayerResult {
  /** The GeoJSON layer instance */
  layer: GeoJSONLayer;

  /** The Blob URL used by the layer. Must be revoked when the layer is removed to prevent memory leaks. */
  blobUrl: string;
}

/**
 * @returns GeoJSONLayer instance created from the provided world map entities and fields,
 * along with the Blob URL that must be revoked when the layer is removed.
 */
export function getLayer<T extends WorldMapEntity>({
  entities,
  fields,
  mapStyle,
}: GetLayerProps<T>): GetLayerResult {
  const featureCollection = getWorldMapFeatureCollection(entities);
  const blobUrl = getWorldMapGeoJSONLayerUrl(featureCollection);
  const layer = getWorldMapGeoJSONLayer({ url: blobUrl, fields }, mapStyle);
  return { layer, blobUrl };
}

/**
 * Chooses whether the popup should render above, below, or centered on marker Y.
 * If both top and bottom do not have enough vertical space, it falls back to center.
 */
export function getMarkerPopupPlacement(
  markerY: number,
  viewHeight: number
): MarkerPopupPlacement {
  const minTopSpaceWithMargin =
    MARKER_POPUP_MIN_TOP_SPACE_PX + MARKER_POPUP_TOP_SAFE_MARGIN_PX;
  const hasEnoughTopSpace = markerY >= minTopSpaceWithMargin;
  const hasEnoughBottomSpace =
    viewHeight - markerY >= MARKER_POPUP_MIN_TOP_SPACE_PX;

  if (!hasEnoughTopSpace && !hasEnoughBottomSpace) {
    return "center";
  }

  return hasEnoughTopSpace ? "top" : "bottom";
}

/**
 * Chooses whether the popup should render to the left, center, or right of a marker.
 * - Left third of the map: popup opens to the right of marker
 * - Middle third: popup stays centered on marker
 * - Right third: popup opens to the left of marker
 */
export function getMarkerPopupHorizontalPlacement(
  markerX: number,
  viewWidth: number
): MarkerPopupHorizontalPlacement {
  const leftBoundary = viewWidth / MARKER_POPUP_HORIZONTAL_SECTIONS;
  const rightBoundary = (viewWidth / MARKER_POPUP_HORIZONTAL_SECTIONS) * 2;

  if (markerX <= leftBoundary) {
    return "right";
  }

  if (markerX >= rightBoundary) {
    return "left";
  }

  return "center";
}

/** Get horizontal translate value for marker popup placement */
export function getHorizontalTranslate(
  placement: MarkerPopupHorizontalPlacement,
  verticalPlacement?: MarkerPopupPlacement
): string {
  const sideOffset =
    verticalPlacement === "center"
      ? WORLD_MAP_MARKER_WIDTH_PX / 2 + WORLD_MAP_MARKER_POPUP_GAP_PX
      : 0;

  switch (placement) {
    case "right":
      return sideOffset > 0 ? `${sideOffset}px` : "0";
    case "left":
      return sideOffset > 0 ? `calc(-100% - ${sideOffset}px)` : "-100%";
    default:
      return "-50%";
  }
}

/** Get vertical translate value for marker popup placement */
export function getVerticalTranslate(placement: MarkerPopupPlacement): string {
  const markerToPopupDistanceTopPx =
    WORLD_MAP_MARKER_HEIGHT_PX / 2 + WORLD_MAP_MARKER_POPUP_GAP_PX;

  if (placement === "top") {
    return `calc(-100% - ${markerToPopupDistanceTopPx}px)`;
  }

  if (placement === "center") {
    return "-50%";
  }

  return `${WORLD_MAP_MARKER_POPUP_GAP_PX}px`;
}

/**
 * Returns true if the given graphic represents a cluster (i.e. `cluster_count > 1`).
 * Used to filter out cluster graphics from hit-test results.
 */
export function isClusterGraphic(graphic: __esri.Graphic): boolean {
  const count = Number(graphic.attributes?.cluster_count);
  return Number.isFinite(count) && count > 1;
}

/**
 * Hit-tests the map at the given screen point and returns the first
 * graphic matching the specified filter, or null if none is found.
 */
export async function hitTestGraphic(
  view: MapView,
  screenPoint:
    | __esri.ScreenPoint
    | __esri.ViewClickEvent
    | __esri.ViewPointerMoveEvent,
  layer?: GeoJSONLayer | null,
  isCluster: boolean = false
): Promise<__esri.Graphic | null> {
  const hitResult = layer
    ? await view.hitTest(screenPoint, { include: [layer] })
    : await view.hitTest(screenPoint);

  const result = hitResult.results.find(
    (r) => r.type === "graphic" && isCluster === isClusterGraphic(r.graphic)
  );

  return result?.type === "graphic" ? result.graphic : null;
}

/**
 * Converts world map entities to ArcGIS geometries in Web Mercator units (wkid: 3857).
 * @returns Array of ArcGIS geometries Web Mercator units (wkid: 3857).
 * @param entities Array of world map entities to convert.
 */
export function getWebMercatorGeometries<T extends WorldMapEntity>(
  entities: T[]
): __esri.GeometryUnion[] {
  return entities.map((entity) => {
    const geoPoint = new Point({
      longitude: entity.position.long,
      latitude: entity.position.lat,
      spatialReference: { wkid: 4326 },
    });
    return geographicToWebMercator(geoPoint);
  });
}

/**
 * Reads the user's previously selected map style from local storage.
 *
 * @returns The stored map style or the default map style if none is found.
 */
export function getStoredMapStyle(): MapStyle {
  const storedMapStyle = LocalStorageUtils.getStringItem(MAP_STYLE_STORAGE_KEY);
  if (!isMapStyle(storedMapStyle)) {
    return WORLD_MAP_DEFAULT_MAP_STYLE;
  }
  return storedMapStyle;
}

/** Persists the user's selected map style to local storage. */
export function setStoredMapStyle(mapStyle: MapStyle): void {
  LocalStorageUtils.setStringItem(MAP_STYLE_STORAGE_KEY, mapStyle);
}
